import type {
  ConfirmBankLimitProposalLineRequest,
  ConfirmBankLimitProposalRequest,
} from '@interfaces/bank-limits/requests/bank-limit-update-proposal.request';
import type {
  BankLimitProposalCurrentLimitResponse,
  BankLimitProposalLineResponse,
  BankLimitProposalResponse,
  BankLimitTypeCode,
} from '@interfaces/bank-limits/responses/bank-limit-update-proposal.response';

/**
 * Regras de tela da modal "Limites informados pelo banco" (card 3787). Valores vêm prontos do
 * backend (o BRL já convertido pelo câmbio); aqui só se valida o que a pessoa digitou, com as
 * mesmas mensagens do 422, e se montam os textos. O backend valida de novo na confirmação.
 */

/** Abaixo disto a tela pede para conferir o valor lido pela IA. */
export const LOW_CONFIDENCE_THRESHOLD = 0.7;

/** O que a pessoa edita em cada tipo de limite. Datas `YYYY-MM-DD`. */
export interface ProposalLineFormValue {
  limitTypeCode: BankLimitTypeCode;
  apply: boolean;
  amountBrl: number | null;
  validityStartDate: string | null;
  validityEndDate: string | null;
}

/** "4131" / "derivativos": como o tipo entra nas frases ("limite de 4131"). */
export function limitTypePhrase(code: BankLimitTypeCode | string): string {
  switch (code) {
    case 'Loan4131':
      return '4131';
    case 'Derivatives':
      return 'derivativos';
    default:
      return String(code);
  }
}

/** Título da seção do tipo: "Empréstimo 4131" / "Derivativos". */
export function limitTypeTitle(code: BankLimitTypeCode | string): string {
  switch (code) {
    case 'Loan4131':
      return 'Empréstimo 4131';
    case 'Derivatives':
      return 'Derivativos';
    default:
      return String(code);
  }
}

/** Valor inicial do formulário: marcado e preenchido com o que o banco informou quando `found`. */
export function initialLineValue(line: BankLimitProposalLineResponse): ProposalLineFormValue {
  return {
    limitTypeCode: line.limitTypeCode,
    apply: line.found,
    amountBrl: line.found ? line.proposedAmountBrl : null,
    validityStartDate: line.found ? line.validityStartDate : null,
    validityEndDate: line.found ? line.validityEndDate : null,
  };
}

const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const amountFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `40000000` → `"R$ 40.000.000,00"` (espaço normal, não o NBSP do Intl). */
export function formatBrl(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return brlFormatter.format(value).replace(/\u00a0/g, ' ');
}

/** `"2027-09-30"` → `"30/09/2027"`; vazia ou inválida, `""`. Data de calendário, sem fuso. */
export function formatIsoDate(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/**
 * "Banco informou USD 10.000.000,00 · Convertido pela PTAX de venda de 28/10/2026 (5,4321)." só quando
 * a moeda não é BRL. A nota da conversão vem pronta do backend (`fxRateNote`): o front não monta câmbio.
 */
export function reportedForeignAmountText(line: BankLimitProposalLineResponse): string | null {
  const currency = (line.currencyCode ?? '').toUpperCase();
  if (!line.found || !currency || currency === 'BRL' || line.proposedAmount === null) {
    return null;
  }
  const note = line.fxRateNote?.trim() ? ` · ${line.fxRateNote.trim()}` : '';
  return `Banco informou ${currency} ${amountFormatter.format(line.proposedAmount)}${note}`;
}

/** "Hoje: R$ 40.000.000,00 até 30/09/2026" ou "Hoje: sem limite cadastrado". */
export function currentLimitText(current: BankLimitProposalCurrentLimitResponse | null): string {
  if (!current) {
    return 'Hoje: sem limite cadastrado';
  }
  const end = formatIsoDate(current.validityEndDate);
  return end
    ? `Hoje: ${formatBrl(current.amountBrl)} até ${end}`
    : `Hoje: ${formatBrl(current.amountBrl)}, sem fim de vigência`;
}

/** Trecho do e-mail ao lado do formulário. */
export interface ProposalExcerptView {
  text: string | null;
  emptyText: string;
  lowConfidence: boolean;
}

export const LOW_CONFIDENCE_TEXT = 'Confira: a leitura deste valor teve baixa confiança.';

export function excerptView(line: BankLimitProposalLineResponse): ProposalExcerptView {
  const text = line.sourceExcerpt?.trim() || null;
  return {
    text,
    emptyText: 'O e-mail não fala deste limite.',
    lowConfidence: !!text && line.confidence !== null && line.confidence < LOW_CONFIDENCE_THRESHOLD,
  };
}

/** Maior valor marcado que passa do limite global, ou `null` se nenhum passa (ou não há global). */
export function amountAboveGlobal(
  lines: readonly ProposalLineFormValue[],
  creditGlobal: number | null | undefined
): number | null {
  if (creditGlobal === null || creditGlobal === undefined) {
    return null;
  }
  const above = lines
    .filter(line => line.apply && line.amountBrl !== null && line.amountBrl > creditGlobal)
    .map(line => line.amountBrl as number);
  return above.length ? Math.max(...above) : null;
}

/**
 * Erros da confirmação, na ordem do formulário, com as mesmas frases do 422 do backend. Vazio =
 * pode confirmar. Datas comparadas como texto `YYYY-MM-DD` (mesma ordem do calendário).
 */
export function validateProposalConfirmation(
  lines: readonly ProposalLineFormValue[],
  options: { bankName: string; creditGlobal: number | null | undefined; raiseGlobalLimit: boolean }
): string[] {
  const applied = lines.filter(line => line.apply);
  if (applied.length === 0) {
    return ['Marque ao menos um limite para atualizar, ou use Rejeitar.'];
  }

  const errors: string[] = [];
  for (const line of applied) {
    const type = limitTypePhrase(line.limitTypeCode);
    if (line.amountBrl === null || !Number.isFinite(line.amountBrl) || line.amountBrl <= 0) {
      errors.push(`Informe o valor do limite de ${type} em reais.`);
    }
    if (!line.validityStartDate) {
      errors.push(`Informe o início da vigência do limite de ${type}.`);
    }
    if (
      !line.validityEndDate ||
      (line.validityStartDate && line.validityEndDate <= line.validityStartDate)
    ) {
      errors.push(`Informe o fim da vigência do limite de ${type}, depois do início.`);
    }
    const global = options.creditGlobal;
    if (
      !options.raiseGlobalLimit &&
      global !== null &&
      global !== undefined &&
      line.amountBrl !== null &&
      line.amountBrl > global
    ) {
      errors.push(
        `O limite de ${type} informado (${formatBrl(line.amountBrl)}) é maior que o limite global do ${options.bankName} (${formatBrl(global)}). Marque 'Atualizar também o limite global' ou ajuste o valor.`
      );
    }
  }
  return errors;
}

/** Corpo do `PUT confirm`: todas as linhas, as desmarcadas com `apply: false` e sem valores. */
export function buildConfirmPayload(
  lines: readonly ProposalLineFormValue[],
  raiseGlobalLimit: boolean
): ConfirmBankLimitProposalRequest {
  return {
    lines: lines.map(
      (line): ConfirmBankLimitProposalLineRequest => ({
        limitTypeCode: line.limitTypeCode,
        apply: line.apply,
        amountBrl: line.apply ? line.amountBrl : null,
        validityStartDate: line.apply ? line.validityStartDate : null,
        validityEndDate: line.apply ? line.validityEndDate : null,
      })
    ),
    raiseGlobalLimit,
  };
}

/**
 * Quem respondeu, para o subtítulo: o remetente real do e-mail (cabeçalho From), nunca o nome que a
 * IA leu do corpo, que quem escreveu o e-mail controla (card 3787, G2). Com remetente fora do
 * cadastro do banco (`senderIsRegisteredContact === false`), só o e-mail: o nome de exibição do
 * cabeçalho também é escolhido por quem enviou e não pode aparecer como se fosse confiável (crítica
 * de design, G2). A faixa de aviso explica o resto.
 */
export function responderLabel(
  proposal: Pick<BankLimitProposalResponse, 'responderName' | 'responderEmail'> &
    Partial<Pick<BankLimitProposalResponse, 'senderIsRegisteredContact'>>
): string {
  const name = proposal.responderName?.trim();
  const email = proposal.responderEmail?.trim();
  if (proposal.senderIsRegisteredContact === false) return email || 'remetente não identificado';
  if (name && email) return `${name} (${email})`;
  return name || email || 'banco';
}

/** Texto do passo de confirmação antes de gravar limites de um remetente fora do cadastro (G3). */
export interface UnregisteredSenderConfirmation {
  title: string;
  description: string;
  cancelLabel: string;
  confirmLabel: string;
}

/**
 * Confirmação extra de "Confirmar e atualizar limites" quando o remetente não é contato cadastrado
 * do banco (crítica de design, G3). `null` quando o remetente é contato cadastrado ou a proposta é
 * anterior à regra: aí a confirmação grava direto, como antes.
 */
export function unregisteredSenderConfirmation(
  proposal: Pick<
    BankLimitProposalResponse,
    'senderIsRegisteredContact' | 'responderEmail' | 'bankName'
  >
): UnregisteredSenderConfirmation | null {
  if (proposal.senderIsRegisteredContact !== false) {
    return null;
  }
  const sender = proposal.responderEmail?.trim() || 'O remetente desta resposta';
  return {
    title: 'Confirmar limites de um remetente não cadastrado?',
    description:
      `${sender} não é contato cadastrado do ${proposal.bankName}. ` +
      `Ao confirmar, os valores marcados passam a ser os limites do ${proposal.bankName} no ` +
      'Relacionamento Bancário. Só confirme se você já conferiu estes valores com o banco.',
    cancelLabel: 'Voltar',
    confirmLabel: 'Confirmar mesmo assim',
  };
}

/**
 * Aviso de remetente fora do cadastro (G2): `null` quando o remetente é contato cadastrado do banco
 * ou quando a proposta é anterior à regra (sem a informação).
 */
export function senderWarningText(
  proposal: Pick<
    BankLimitProposalResponse,
    'senderIsRegisteredContact' | 'responderEmail' | 'bankName' | 'extractedResponderName'
  >
): string | null {
  if (proposal.senderIsRegisteredContact !== false) {
    return null;
  }
  const sender = proposal.responderEmail?.trim() || 'desta resposta';
  const signed = proposal.extractedResponderName?.trim()
    ? ` O e-mail vem assinado por "${proposal.extractedResponderName.trim()}", mas isso não confirma quem enviou.`
    : '';
  return `O remetente ${sender} não é contato cadastrado do ${proposal.bankName}. Confira com o banco antes de confirmar.${signed}`;
}

/** "Confirmada por Ana em 29/10/2026" / "Rejeitada por Ana em 29/10/2026: motivo". `null` se Pending. */
export function decisionText(
  proposal: Pick<
    BankLimitProposalResponse,
    'status' | 'decidedByUserName' | 'decidedAt' | 'rejectionReason'
  >,
  formatDate: (isoDateTime: string) => string
): string | null {
  if (proposal.status === 'Pending') {
    return null;
  }
  const who = proposal.decidedByUserName ? ` por ${proposal.decidedByUserName}` : '';
  const when = proposal.decidedAt ? ` em ${formatDate(proposal.decidedAt)}` : '';
  if (proposal.status === 'Confirmed') {
    return `Confirmada${who}${when}. Os limites abaixo foram gravados no Relacionamento Bancário.`;
  }
  const reason = proposal.rejectionReason?.trim()
    ? ` Motivo: ${proposal.rejectionReason.trim()}`
    : '';
  return `Rejeitada${who}${when}. O limite cadastrado não mudou.${reason}`;
}

/** Faixa "Hoje → Novo" no topo de cada tipo (crítica G2): o que muda se confirmar. */
export interface LimitChangeView {
  currentText: string;
  newText: string;
  direction: 'up' | 'down' | 'same' | 'new' | 'unchanged';
  directionLabel: string;
}

function amountUntil(amount: number | null, end: string | null): string {
  const value = formatBrl(amount);
  const until = formatIsoDate(end);
  return until ? `${value} até ${until}` : value;
}

/**
 * Compara o limite cadastrado hoje com o que vai ser gravado (o valor digitado, ou o gravado na
 * confirmação). Só compara os dois números para dizer se sobe ou desce: nenhuma conta.
 */
export function limitChangeView(
  current: BankLimitProposalCurrentLimitResponse | null,
  next: { apply: boolean; amountBrl: number | null; validityEndDate: string | null }
): LimitChangeView {
  const currentText = current
    ? amountUntil(current.amountBrl, current.validityEndDate)
    : 'Sem limite cadastrado';

  if (!next.apply || next.amountBrl === null) {
    return { currentText, newText: 'Não muda', direction: 'unchanged', directionLabel: '' };
  }

  const newText = amountUntil(next.amountBrl, next.validityEndDate);
  if (!current) {
    return { currentText, newText, direction: 'new', directionLabel: 'Limite novo' };
  }
  if (next.amountBrl > current.amountBrl) {
    return { currentText, newText, direction: 'up', directionLabel: 'Aumenta' };
  }
  if (next.amountBrl < current.amountBrl) {
    return { currentText, newText, direction: 'down', directionLabel: 'Reduz' };
  }
  return { currentText, newText, direction: 'same', directionLabel: 'Mesmo valor' };
}

/** Explicação do Rejeitar (crítica G4): não manda a um botão que pode não existir para o banco. */
export const REJECT_HINT =
  'O limite cadastrado não muda. O próximo pedido sai pela agenda; se o limite do banco estiver vencido ou sem cadastro, use "Pedir atualização ao banco" no card de Limites da Home.';
